import { ReactNode, useMemo } from 'react'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { MonthSelector } from '../MonthSelector'
import * as styles from '../styles'
import { type CalendarContextValue, useCalendar } from '../useCalendar'
import { DateActions } from '../useCalendar/useCalendarDates'
import { WeekHeader } from '../WeekHeader'
import { Day } from './Day'

const mapKeypressToAction = (
  e: React.KeyboardEvent<HTMLTableElement>
): DateActions | null => {
  const { key, shiftKey } = e

  switch (key) {
    case 'PageUp':
      return shiftKey ? DateActions.NextYear : DateActions.NextMonth
    case 'PageDown':
      return shiftKey ? DateActions.PrevYear : DateActions.PrevMonth
    case 'ArrowDown':
      return DateActions.NextWeek
    case 'ArrowUp':
      return DateActions.PrevWeek
    case 'ArrowRight':
      return DateActions.NextDay
    case 'ArrowLeft':
      return DateActions.PrevDay
    default:
      return null
  }
}

const buildFormatters = (locale: IETFLanguageTag | undefined) => ({
  dayNameScreenreader: new Intl.DateTimeFormat(locale, {
    weekday: 'long',
  }),
  dayNameVisual: new Intl.DateTimeFormat(locale, {
    weekday: 'narrow',
  }),
  dateScreenreader: new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
  }),
  dateVisual: new Intl.DateTimeFormat(locale, {
    day: 'numeric',
  }),
})

type MonthProps = {
  children?: (context: CalendarContextValue) => ReactNode
}

export const Month: React.VFC<MonthProps> = ({ children }) => {
  const calendarContext = useCalendar()
  const {
    advanceFromKeypress,
    locale,
    tableRef,
    focusDate,
    calendarDateGrid,
    selectDate,
    setFocusDate,
  } = calendarContext

  const formatters = useMemo(() => buildFormatters(locale), [locale])
  const {
    dayNameScreenreader: dayNameScreenreaderFormatter,
    dayNameVisual: dayNameVisualFormatter,
    dateScreenreader: dateScreenreaderFormatter,
    dateVisual: dateVisualFormatter,
  } = formatters

  const handleKeypress: React.KeyboardEventHandler<HTMLTableElement> = (e) => {
    if (!(e.target instanceof Element)) return

    const action = mapKeypressToAction(e)
    if (action !== null) {
      advanceFromKeypress(action)
      return
    }

    if (e.key === 'Enter') {
      const focusedCell = e.target.closest('td[data-date]')
      if (!focusedCell) return

      const timestamp = focusedCell.getAttribute('data-date')
      if (timestamp) selectDate(timestamp)
    }
  }

  const handleCellClick: React.MouseEventHandler = (e) => {
    if (!(e.target instanceof Element)) return
    const clickedCell = e.target.closest('td[data-date]')

    if (!clickedCell) return

    const timestamp = clickedCell.getAttribute('data-date')
    if (timestamp) selectDate(timestamp)
  }

  return (
    <div className={styles.selectors.monthContainer}>
      <MonthSelector focusDate={focusDate} setFocusDate={setFocusDate} />
      <table
        className={styles.selectors.table}
        onClick={handleCellClick}
        onKeyDown={handleKeypress}
        ref={tableRef}
        role="grid"
      >
        <WeekHeader
          screenreaderFormatter={dayNameScreenreaderFormatter}
          visualFormatter={dayNameVisualFormatter}
        />
        <tbody>
          {calendarDateGrid.map((week) => (
            <tr
              className={styles.selectors.week}
              key={week[0].date.toISOString()}
            >
              {week.map(({ date, ref }) => (
                <Day
                  date={date}
                  key={date.toISOString()}
                  ref={ref}
                  screenreaderFormatter={dateScreenreaderFormatter}
                  visualFormatter={dateVisualFormatter}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {children?.(calendarContext)}
    </div>
  )
}

Month.displayName = 'Lapidary: Month ← Calendar'
